// @purpose VIEW
// The Daylight stage (templates/consumer/daylight/_stage.html): three chibi specimens, one message.
// - They wake up and follow the pointer (eyes + a small lean), or look toward a tap on touch screens.
// - Send splits the message into three bubbles that fly to each one's phone; the phones ding (ripple, a little
//   shake, a short vibration where the device supports it, a soft chime), then each face changes to its
//   temperament (bright / worried / stoic), the body reacts (bounce / soak / hold / sway / buzz / glow) and the
//   reactions appear. Send again replays it. Tap one specimen to see only its reaction.
// Reduced motion: no following, no flight; the result appears at once. Without JS the reactions just show.

const FLY_MS = 620;
const FACE_MS = 860;
const LAND_MS = 1300;

let audio;
function chime(count) {
  try {
    audio = audio || new (window.AudioContext || window.webkitAudioContext)();
    const now = audio.currentTime;
    for (let i = 0; i < count; i += 1) {
      const t = now + i * 0.11;
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(1318.5, t);
      osc.frequency.exponentialRampToValueAtTime(1760, t + 0.08);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.045, t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
      osc.connect(gain).connect(audio.destination);
      osc.start(t);
      osc.stop(t + 0.24);
    }
  } catch {
    /* no audio: the visual ding is enough */
  }
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

function setup(stage) {
  if (stage.dataset.js) return;
  stage.dataset.js = "1";
  stage.dataset.state = "ready";
  const specs = [...stage.querySelectorAll("[data-dl-spec]")];
  const send = stage.querySelector("[data-dl-send]");
  const hint = stage.querySelector("[data-dl-hint]");
  const note = stage.querySelector("[data-dl-note]");
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const setFace = (face) => specs.forEach((s) => (s.dataset.face = face));
  setFace("calm");

  // --- follow the pointer -------------------------------------------------------------------------------
  let frame = 0;
  let last = null;
  const look = () => {
    frame = 0;
    if (!last) return;
    for (const s of specs) {
      const r = (s.querySelector("[data-dl-body]") || s).getBoundingClientRect();
      const dx = last.x - (r.left + r.width / 2);
      const dy = last.y - (r.top + r.height * 0.45);
      s.style.setProperty("--lx", `${clamp(dx / 90, -3.2, 3.2)}px`);
      s.style.setProperty("--ly", `${clamp(dy / 120, -2.4, 2.4)}px`);
      s.style.setProperty("--lean", `${clamp(dx / 260, -1, 1) * 5}deg`);
    }
  };
  const wake = (x, y) => {
    last = { x, y };
    if (stage.dataset.state === "ready" && specs[0].dataset.face === "calm") setFace("awake");
    if (!frame) frame = requestAnimationFrame(look);
  };
  if (!reduce) {
    window.addEventListener("pointermove", (e) => wake(e.clientX, e.clientY), { passive: true });
    stage.addEventListener("pointerdown", (e) => wake(e.clientX, e.clientY), { passive: true });
  }

  // --- send ---------------------------------------------------------------------------------------------
  const land = () => {
    specs.forEach((s) => {
      s.dataset.face = s.dataset.temperament;
      s.classList.remove("is-ding");
    });
    stage.dataset.state = "landed";
    window.setTimeout(() => {
      if (note) note.textContent = note.dataset.text || "";
      if (hint) hint.textContent = hint.dataset.again || hint.textContent;
    }, reduce ? 0 : LAND_MS - FACE_MS);
  };

  const fly = () => {
    const from = send.getBoundingClientRect();
    const box = stage.getBoundingClientRect();
    specs.forEach((s, i) => {
      const phone = s.querySelector("[data-dl-phone]").getBoundingClientRect();
      const b = document.createElement("span");
      b.className = "dl-fly";
      b.setAttribute("aria-hidden", "true");
      b.style.left = `${from.left - box.left + from.width / 2}px`;
      b.style.top = `${from.top - box.top + from.height / 2}px`;
      b.style.setProperty("--dx", `${phone.left + phone.width / 2 - (from.left + from.width / 2)}px`);
      b.style.setProperty("--dy", `${phone.top + phone.height / 2 - (from.top + from.height / 2)}px`);
      b.style.animationDelay = `${i * 90}ms`;
      stage.appendChild(b);
      window.setTimeout(() => b.remove(), FLY_MS + 400);
    });
  };

  send?.addEventListener("click", () => {
    stage.removeAttribute("data-focused");
    specs.forEach((s) => s.classList.remove("is-focus"));
    if (note) note.textContent = "";
    if (reduce) {
      land();
      return;
    }
    setFace("awake");
    stage.dataset.state = "sending";
    if (hint) hint.textContent = hint.dataset.sent || hint.textContent;
    fly();
    window.setTimeout(() => {
      specs.forEach((s) => s.classList.add("is-ding"));
      if (navigator.vibrate) navigator.vibrate([25, 40, 25]);
      chime(specs.length);
    }, FLY_MS);
    window.setTimeout(land, FACE_MS);
  });

  specs.forEach((spec) => {
    spec.addEventListener("click", () => {
      const on = !spec.classList.contains("is-focus");
      specs.forEach((s) => s.classList.toggle("is-focus", s === spec && on));
      stage.toggleAttribute("data-focused", on);
      if (stage.dataset.state === "ready") {
        stage.dataset.state = "peek";
        spec.dataset.face = spec.dataset.temperament;
      }
    });
  });
}

export function initDaylight() {
  const run = () => document.querySelectorAll("[data-dl-stage]").forEach(setup);
  run();
  document.addEventListener("htmx:afterSettle", run);
}
